import {UserV3} from '@/features/authentication/types'
import {GoalStatusesE, SupporterTypeV3} from '@/features/goal/types'
import {ALL_LINKS} from '@/utilities/constants/regex'
import dayjs from 'dayjs'
import {
    Channel,
    ChannelsTypesE,
    ChannelSubscriptionTypesE,
    GetGroupsResponse,
    GoalV3,
    Group,
    Message,
    SubscriptionStatusesE
} from '../chat.model'
import {queryClient} from '@/queryClient'
import {InfiniteData} from '@tanstack/react-query'
import {QUERY_KEYS} from '../queries/keys'
import {generateChatChannelName, getCorrectUserId} from '@/utilities/helpers'
import {QUERY_KEYS as NOTIFICATION_QUERY_KEYS} from '@/features/notifications/queries/keys'
import {QUERY_KEYS as GOAL_QUERY_KEYS} from '@/features/goal/queries/keys'

import {NotificationsType} from '@/features/notifications/types'
import {LocalStorageManager} from '@/utilities/localStorage'
import {SSEEventDonateGoalProps} from '@/integrations/SSE/helpers'

// export enum SubscriptionStatusesE {
//     ACTIVE = 'active',
//     CANCELED = 'canceled',
//     REACTIVABLE = 'reactivable',
//     PAUSED = 'paused',
//     FAILED = 'failed',
//     FREE = 'free',
//     INACTIVE = 'inactive'
// }

// export enum SubscriptionTypesE {
//     SUBSCRIPTION = 'subscription',
//     REFERRAL = 'referral',
//     FREE_CONNECTION = 'free_connection'
// }

export const getSubscriptionStatus = (channelDetails: Channel | null) => {
    if (!channelDetails) return
    const subStatus = channelDetails?.metadata?.subscription?.status
    const type = channelDetails.type

    // FREE AND PAUSED
    // type:  free_connection
    // subscription_status: null
    // is_paused: true
    /*TODO: verify logic in backend*/
    if (
        (type === ChannelSubscriptionTypesE.FREE_CONNECTION ||
            type === ChannelSubscriptionTypesE.REFERRAL ||
            type === ChannelSubscriptionTypesE.SUBSCRIPTION) &&
        !subStatus &&
        channelDetails.isPaused
    ) {
        return SubscriptionStatusesE.PAUSED
    }
    // OPEN & FREE
    // type:  free_connection
    // subscription_status: null
    // valid_until_at: null
    if (type === ChannelSubscriptionTypesE.FREE_CONNECTION && !subStatus) {
        return SubscriptionStatusesE.FREE
    }

    // OPEN & FREE - Referred
    // type:  referral
    // subscription_status: null
    // valid_until_at: null
    if (type === ChannelSubscriptionTypesE.REFERRAL && !subStatus) {
        return SubscriptionStatusesE.FREE
    }

    // OPEN BY A GOAL
    // type:  subscription
    // subscription_status: null
    // valid_until_at: null
    if (type === ChannelSubscriptionTypesE.SUBSCRIPTION && !subStatus) {
        return SubscriptionStatusesE.FREE
    }

    // FAILED REBILL
    // type:  subscription
    // subscription_status: 'failed'
    if (type === ChannelSubscriptionTypesE.SUBSCRIPTION && subStatus === SubscriptionStatusesE.FAILED) {
        return SubscriptionStatusesE.FREE
    }

    // PAID  (WAS FREE)
    // type:  free_connection
    // subscription_status: "active"
    // valid_until_at: null
    if (subStatus === SubscriptionStatusesE.ACTIVE && !channelDetails?.metadata?.subscription?.validUntilAt) {
        return SubscriptionStatusesE.ACTIVE
    }
    // CANCELED FROM PAID (WAS FREE)
    // type:  free_connection
    // subscription_status: "canceled"
    // valid_until_at: 2022-09-04 09:40:47
    // NOTE: we are assuming that `valid_until_at` is UTC 0
    if (subStatus !== 'active' && channelDetails?.metadata?.subscription?.validUntilAt) {
        const isReactivable =
            dayjs
                .utc(dayjs(channelDetails?.metadata?.subscription?.validUntilAt).format('YYYY-MM-DD hh:mm:ss'))
                .diff(dayjs(), 'seconds') > 0
        if (isReactivable) {
            // ( is reactivable )
            return SubscriptionStatusesE.REACTIVABLE
        }
        return SubscriptionStatusesE.CANCELED
    }

    return null
}

export const getActiveGoalByUserRole = (goalsList: GoalV3[]) => {
    // Check if user has an active goal

    const activeGoal = goalsList?.find(goal => goal.status === GoalStatusesE.ACTIVE)

    return activeGoal
}

export const checkIfMessageIncludeAllLink = (message: string) => {
    return message?.search(ALL_LINKS) !== -1
}

export const updateChannelLastMessageOnly = (
    receivedMessage: Message,
    currentUser: UserV3,
    creatorId: number,
    followerId: number,
    queryKey: string[]
) => {
    queryClient.setQueryData<
        InfiniteData<{
            normalizedChannels: Record<string, Channel>
            channelsList: Channel[]
            nextPage?: number
            total: number
        }>
    >(queryKey, oldData => {
        if (!oldData) {
            return undefined
        }
        const currentUserId = getCorrectUserId(currentUser)
        const receiverId = currentUserId === creatorId ? followerId : creatorId
        const channelPk = generateChatChannelName(currentUserId, currentUser.type, receiverId)

        let channelFound = false

        const updatedPages = oldData.pages.map(page => {
            const newChannelsList = page.channelsList.map(ch => {
                if (ch.pk === channelPk) {
                    channelFound = true
                    return {
                        ...ch,
                        lastMessage: receivedMessage,
                        updatedAt: new Date(receivedMessage.sentAt).toISOString()
                    }
                }
                return ch
            })
            return {
                ...page,
                channelsList: newChannelsList,
                normalizedChannels: Object.fromEntries(newChannelsList.map(ch => [ch.pk, ch]))
            }
        })

        if (!channelFound) {
            return oldData
        }
        return {...oldData, pages: updatedPages}
    })
}

export const increaseUnreadMessageCount = (currentUser: UserV3, senderId: number) => {
    const currentUserId = getCorrectUserId(currentUser)
    const chatUserId = LocalStorageManager.chatUserId.get()
    if (senderId !== currentUserId && senderId !== chatUserId) {
        const unreadCountQueryKey = [QUERY_KEYS.fetchUnreadMessageCountV3, chatUserId || currentUserId]

        queryClient.setQueryData<{unreadMessagesCount: number} | undefined>(unreadCountQueryKey, oldData => {
            if (oldData) {
                return {...oldData, unreadMessagesCount: oldData.unreadMessagesCount + 1}
            }
            queryClient.invalidateQueries({queryKey: unreadCountQueryKey})
            return undefined
        })
    }
}

export const reorderChannelsAndReplaceLastMessage = (
    receivedMessage: Message,
    currentUser: UserV3,
    creatorId: number,
    followerId: number,
    queryKey: string[]
) => {
    queryClient.setQueryData<
        InfiniteData<{
            normalizedChannels: Record<string, Channel>
            channelsList: Channel[]
            nextPage?: number
            total: number
        }>
    >(queryKey, oldData => {
        if (!oldData) return oldData

        const currentUserId = getCorrectUserId(currentUser)
        const receiverId = currentUserId === creatorId ? followerId : creatorId
        const channelPk = generateChatChannelName(currentUserId, currentUser.type, receiverId)

        let targetChannel: Channel | undefined
        const updatedPages = oldData.pages.map(page => {
            const newChannelsList = page.channelsList.filter(ch => {
                const chPk = ch.pk
                if (chPk === channelPk) {
                    targetChannel = ch
                    return false
                }
                return true
            })
            return {
                ...page,
                channelsList: newChannelsList,
                normalizedChannels: Object.fromEntries(newChannelsList.map(ch => [ch.pk, ch]))
            }
        })

        if (!targetChannel) {
            queryClient.invalidateQueries({queryKey})
            return oldData
        }

        const isMessageMine = receivedMessage.senderId === currentUserId

        const newUnreadCount = !isMessageMine
            ? (targetChannel.metadata.unreadMessagesCount || 0) + 1
            : targetChannel.metadata.unreadMessagesCount || 0

        const updatedTargetChannel: Channel = {
            ...targetChannel,
            lastMessage: receivedMessage,
            metadata: {
                ...targetChannel.metadata,
                unreadMessagesCount: newUnreadCount
            }
        }

        updatedPages[0].channelsList.unshift(updatedTargetChannel)
        updatedPages[0].normalizedChannels[channelPk] = updatedTargetChannel

        return {...oldData, pages: updatedPages}
    })
}

export const showMessageInChat = (message: Message, creatorId: number, followerId: number, user: UserV3) => {
    const currentUserId = getCorrectUserId(user)
    const receiverId = currentUserId === creatorId ? followerId : creatorId
    const channelPk = generateChatChannelName(currentUserId, user.type, receiverId)
    queryClient.setQueryData<InfiniteData<Message[]> | undefined>(
        [QUERY_KEYS.fetchMessages, String(channelPk)],
        oldData => {
            if (!oldData) {
                queryClient.invalidateQueries({queryKey: [QUERY_KEYS.fetchMessages, String(channelPk)]})
                return oldData
            }
            const updatedPages = oldData.pages.map((page, index) => {
                if (index === 0) {
                    return [message, ...page]
                }
                return page
            })
            return {...oldData, pages: updatedPages}
        }
    )
}
export const replaceMessageInChat = (message: Message, channelPk: string): {isLastMessage: boolean} => {
    let isLastMessage = false
    queryClient.setQueryData<InfiniteData<Message[]> | undefined>(
        [QUERY_KEYS.fetchMessages, String(channelPk)],
        oldData => {
            if (!oldData) {
                queryClient.invalidateQueries({queryKey: [QUERY_KEYS.fetchMessages, String(channelPk)]})
                return oldData
            }
            const updatedPages = oldData.pages.map(page =>
                page.map((oldMessage, index) => {
                    if (oldMessage.id === message.id) {
                        if (index === 0) isLastMessage = true
                        return message
                    }
                    return oldMessage
                })
            )
            return {...oldData, pages: updatedPages}
        }
    )
    return {isLastMessage}
}
export const replaceLastMessage = (receivedMessage: Message, queryKey: string[]) => {
    queryClient.setQueryData<
        InfiniteData<{
            normalizedChannels: Record<string, Channel>
            channelsList: Channel[]
            nextPage?: number
            total: number
        }>
    >(queryKey, oldData => {
        if (!oldData) return oldData
        const updatedPages = oldData.pages.map(page => ({
            ...page,
            channelsList: page.channelsList.map(channel =>
                channel.lastMessage && channel.lastMessage.id === receivedMessage.id
                    ? {...channel, lastMessage: receivedMessage}
                    : channel
            )
        }))

        return {...oldData, pages: updatedPages}
    })
}
export const messageEventHandler = (message: Message, user: UserV3, creatorId: number, followerId: number) => {
    increaseUnreadMessageCount(user, message.senderId)
    showMessageInChat(message, creatorId, followerId, user)
    const chatUserId = LocalStorageManager.chatUserId.get()
    reorderChannelsAndReplaceLastMessage(message, user, creatorId, followerId, [
        QUERY_KEYS.fetchChannels,
        String(chatUserId || user?.id),
        ChannelsTypesE.ACTIVE,
        ''
    ])
    reorderChannelsAndReplaceLastMessage(message, user, creatorId, followerId, [
        QUERY_KEYS.fetchChannels,
        String(chatUserId || user?.id),
        ChannelsTypesE.PAID,
        ''
    ])
    reorderChannelsAndReplaceLastMessage(message, user, creatorId, followerId, [
        QUERY_KEYS.fetchChannels,
        String(chatUserId || user?.id),
        ChannelsTypesE.INACTIVE,
        ''
    ])
}

type MessageUpdater = (msg: Message) => Message

export const generateQueryKey = (senderId: number, userType: string, receiverId: number) => [
    QUERY_KEYS.fetchMessages,
    String(generateChatChannelName(senderId, userType, receiverId))
]

export const updateMessageInCache = (
    senderId: number,
    receiverId: number,
    userType: string,
    updateFn: MessageUpdater
) => {
    const queryKey = generateQueryKey(senderId, userType, receiverId)

    queryClient.setQueryData<InfiniteData<Message[]> | undefined>(queryKey, oldData => {
        if (!oldData) return oldData

        const updatedPages = oldData.pages.map(page => page.map(msg => updateFn(msg)))

        return {...oldData, pages: updatedPages}
    })
}

export const unlockedAttachmentEventHandler = (
    receiverId: number,
    senderId: number,
    currentUser: UserV3,
    attachmentId?: number
) => {
    updateMessageInCache(senderId, receiverId, currentUser.type, (msg: Message) => {
        const attachment = msg.metadata?.chatAttachment
        if (attachment?.id === attachmentId) {
            return {
                ...msg,
                metadata: {
                    ...msg.metadata,
                    chatAttachment: {
                        ...attachment,
                        isPaid: true
                    }
                }
            }
        }
        return msg
    })
}

export const unlockedPaidMessageEventHandler = (
    receiverId: number,
    senderId: number,
    currentUser: UserV3,
    paidMessageId?: number
) => {
    updateMessageInCache(senderId, receiverId, currentUser.type, (msg: Message) => {
        const metaPaidId = msg.metadata?.paidMessage?.id

        if (msg.paidMessageId === paidMessageId && metaPaidId === paidMessageId) {
            return {
                ...msg,
                metadata: {
                    ...msg.metadata,
                    paidMessage: {
                        ...msg.metadata.paidMessage,
                        isPaid: true
                    }
                }
            }
        }
        return msg
    })
}

// helpers/createIntersectionObserver.ts
type CreateObserverParams = {
    fetchNextPage: () => void
    hasNextPage: boolean
    isFetchingNextPage: boolean
    observerRef: React.MutableRefObject<IntersectionObserver | null>
}

export function createIntersectionObserver({
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    observerRef
}: CreateObserverParams) {
    return (node: HTMLDivElement | null): void => {
        if (isFetchingNextPage) return

        if (observerRef.current) observerRef.current.disconnect()

        observerRef.current = new IntersectionObserver(entries => {
            if (entries[0].isIntersecting && hasNextPage) {
                fetchNextPage()
            }
        })

        if (node) observerRef.current.observe(node)
    }
}

export const showMessageInBroadcastOrGroup = (message: Message, query: (string | number)[]) => {
    queryClient.setQueryData<InfiniteData<Message[]> | undefined>(query, oldData => {
        if (!oldData) {
            return {pages: [[message]], pageParams: [undefined]}
        }
        const updatedPages = oldData.pages.map((page, index) => {
            if (index === 0) {
                return [message, ...page]
            }
            return page
        })
        return {...oldData, pages: updatedPages}
    })
}

export const reorderGroupChannelsAndReplaceLastMessage = (message: Message, groupId: number, currentUser: UserV3) => {
    const currentUserId = getCorrectUserId(currentUser)
    queryClient.setQueryData<InfiniteData<GetGroupsResponse> | undefined>(
        [QUERY_KEYS.getGroupListV3, currentUserId],
        oldData => {
            if (!oldData) {
                queryClient.invalidateQueries({queryKey: [QUERY_KEYS.getGroupListV3]})
                return undefined
            }
            let targetGroup: Group | undefined
            const updatedPages = oldData.pages.map(page => {
                const filteredGroups = page.data.filter(group => {
                    if (group.id === groupId) {
                        targetGroup = group
                        return false
                    }
                    return true
                })
                return {
                    ...page,
                    data: filteredGroups
                }
            })

            if (!targetGroup) {
                return oldData
            }

            const updatedTargetGroup: Group = {
                ...targetGroup,
                lastMessage: message,
                updatedAt: message.sentAt
            }

            const newPages = updatedPages.map((page, idx) => {
                if (idx === 0) {
                    return {
                        ...page,
                        data: [updatedTargetGroup, ...page.data]
                    }
                }
                return page
            })

            return {...oldData, pages: newPages}
        }
    )
}

export const broadcastMessageEventHandler = (message: Message, user: UserV3, groupId: number) => {
    showMessageInBroadcastOrGroup(message, [QUERY_KEYS.getBroadcastMessagesV3, groupId])
    reorderGroupChannelsAndReplaceLastMessage(message, groupId, user)
}

export const groupMessageEventHandler = (message: Message, user: UserV3, groupId: number) => {
    showMessageInBroadcastOrGroup(message, [QUERY_KEYS.fetchMessages, groupId])
    reorderGroupChannelsAndReplaceLastMessage(message, groupId, user)
}
export const removeMessageEventHandler = (message: Message, user: UserV3, channelId: string) => {
    const {isLastMessage} = replaceMessageInChat(message, channelId)
    if (isLastMessage) {
        replaceLastMessage(message, [QUERY_KEYS.fetchChannels, String(user?.id), ChannelsTypesE.ACTIVE, ''])
        replaceLastMessage(message, [QUERY_KEYS.fetchChannels, String(user?.id), ChannelsTypesE.PAID, ''])
        replaceLastMessage(message, [QUERY_KEYS.fetchChannels, String(user?.id), ChannelsTypesE.INACTIVE, ''])
    }
}

// received notification manager

export const showNotificationIndicator = () => {
    const unreadCountQueryKey = [NOTIFICATION_QUERY_KEYS.notificationUnreadCount]

    queryClient.setQueryData<{count: number} | undefined>(unreadCountQueryKey, oldData => {
        if (oldData) {
            return {...oldData, count: oldData.count + 1}
        }
        queryClient.invalidateQueries({queryKey: unreadCountQueryKey})
        return undefined
    })
}

export const setNewNotificationToDisplay = (notification: NotificationsType) => {
    queryClient.setQueryData<InfiniteData<NotificationsType[]>>(
        [NOTIFICATION_QUERY_KEYS.notificationsHistory],
        oldData => {
            if (!oldData) return {pageParams: [undefined], pages: [[notification]]}

            const updatedPages = {...oldData}
            updatedPages.pages?.[0].unshift(notification)
            return updatedPages
        }
    )
}

export const notificationEventHandler = (notification: NotificationsType) => {
    showNotificationIndicator()
    setNewNotificationToDisplay(notification)
}

export const donateToGoalEventHandler = (data: SSEEventDonateGoalProps) => {
    queryClient.setQueriesData({queryKey: [GOAL_QUERY_KEYS.selectedGoal, data.goalId.toString()]}, oldData => {
        if (!oldData) return oldData
        return {...oldData, status: data.goalStatus, donationsCount: data.newGoalAmount}
    })
    queryClient.setQueriesData(
        {queryKey: [GOAL_QUERY_KEYS.supportersList, data.goalId.toString()]},
        (oldData: SupporterTypeV3[] | undefined) => {
            if (!oldData) return oldData
            const isHasUserHowSupporter = oldData.find(supporter => supporter.id === data.followerId)
            if (!isHasUserHowSupporter)
                queryClient.invalidateQueries({queryKey: [GOAL_QUERY_KEYS.supportersList, data.goalId.toString()]})

            return oldData.map(supporter =>
                supporter.id === data.followerId
                    ? {
                          ...supporter,
                          typeAttributes: {
                              ...supporter.typeAttributes,
                              leaderDonationsAmount: supporter.typeAttributes.leaderDonationsAmount + data.amount
                          }
                      }
                    : supporter
            )
        }
    )
}
